import { Injectable } from '@angular/core';
import { ImageConfigType } from '@cureskin/ai-response-processor';
import { ConnectionService } from '../connection-service';
import { AiImageProcessorServices } from '../ai-image-processor';
import { AutoComparisonIds, InstantCheckupService } from '../instant-checkup/instant-checkup.service';
import {Broadcaster} from "../../components/broadcaster";
import {AppConfig} from "../../app/app.config";
import {Entity, EntityEnum} from "@cureskin/api-client-rn";
type FaceImageConfig = { before: ImageConfigType; after: ImageConfigType };
type ReportImageConfigEntry = { beforeInstantId?: string; afterInstantId?: string; imageConfig?: FaceImageConfig };

export interface AutoImageConfigsResult {
  instantCheckups: Array<any>;
  primaryIds: Array<string>;
  secondaryIds: Array<string>;
  imageConfigs: Array<ReportImageConfigEntry>;
}

@Injectable({ providedIn: 'root' })
export class FollowUpReportCreationService {
  constructor(
    private readonly instantCheckupService: InstantCheckupService,
    private readonly connectionService: ConnectionService,
    private readonly aiImageProcessorServices: AiImageProcessorServices,
    private readonly broadcasterService: Broadcaster,
    private readonly appConfig: AppConfig,
  ) {}

  async buildAutoImageConfigs(
    user: Table.User,
    type: keyof typeof EntityEnum.InstantCheckup.Type,
    followUpCount: number,
  ): Promise<AutoImageConfigsResult> {
    const ids: AutoComparisonIds = await this.instantCheckupService.getAutoComparisonIds(user, type, followUpCount);
    const result: AutoImageConfigsResult = {
      instantCheckups: ids.instantCheckups,
      primaryIds: ids.primaryIds,
      secondaryIds: ids.secondaryIds,
      imageConfigs: [{}, {}],
    };

    try {
      if (ids.primaryIds.length !== 2) {
        throw new Error(`Can\'t find two ${type}`);
      }

      if (type !== EntityEnum.InstantCheckup.Type.FACE) return result;

      const username = user.get('username');
      const primaryConfig = await this.buildFaceImageConfig(username, ids.primaryIds,
        (checkup: Entity['InstantCheckup']): ImageConfigType => this.aiImageProcessorServices.getFrontFaceImageConfig(checkup));
      result.imageConfigs[0] = {
        imageConfig: primaryConfig.imageConfig,
        beforeInstantId: primaryConfig.beforeInstantId,
        afterInstantId: primaryConfig.afterInstantId,
      };

      if (ids.secondaryIds.length === 2) {
        const secondaryConfig = await this.buildFaceImageConfig(username,
          ids.secondaryIds,
          (checkup: Entity['InstantCheckup']): ImageConfigType => this.aiImageProcessorServices.getSideFaceImageConfig(checkup));
        result.imageConfigs[1] = {
          imageConfig: secondaryConfig.imageConfig,
          beforeInstantId: secondaryConfig.beforeInstantId,
          afterInstantId: secondaryConfig.afterInstantId,
        };
      }
    } catch (error) {
      this.broadcasterService.broadcast('NOTIFY', { message: error.message || error,
        type: this.appConfig.Shared.Toast.Type.ERROR });
    }
    return result;
  }

  private async buildFaceImageConfig(
    username: string,
    instantCheckUpIds: Array<string>,
    getFaceConfig: (checkup: any) => ImageConfigType,
  ): Promise<{ imageConfig?: FaceImageConfig; beforeInstantId?: string; afterInstantId?: string; }> {
    if (instantCheckUpIds.length !== 2) return {};
    const instantCheckups = await this.connectionService.getInstantCheckUpWith(
      username,
      [...instantCheckUpIds],
      ['imagePath', 'compressedImagePath', 'aiResponse', 'dimension', 'type']);
    if ((new Date(instantCheckups[0].createdAt)) > (new Date(instantCheckups[1].createdAt))) {
      instantCheckups.reverse();
    }

    if (!instantCheckups[0]?.aiResponse?.googleResponse?.faceAnnotations?.[0]
      || !instantCheckups[1]?.aiResponse?.googleResponse?.faceAnnotations?.[0]) {
      throw new Error('Image don\'t have AI detections. Select other set of images!');
    }

    const before = getFaceConfig(instantCheckups[0]);
    const after = getFaceConfig(instantCheckups[1]);
    return {
      imageConfig: { before, after },
      beforeInstantId: instantCheckups[0].objectId,
      afterInstantId: instantCheckups[1].objectId,
    };
  }
}
